import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  DocumentReference,
  FirestoreDataConverter,
  getDoc,
  getDocs,
  query,
  QueryDocumentSnapshot,
  SnapshotOptions,
  updateDoc,
  where,
  WithFieldValue,
  writeBatch,
} from "@firebase/firestore";
import { db } from "..";

export type Contact = {
  email?: string;
  date_created: Date;

  id?: string;
  ref?: DocumentReference;
};

export type ContactList = {
  contacts: Contact[];
  date_created: Date;
  title: string;

  id?: string;
  ref?: DocumentReference;
};

export const contactListConverter: FirestoreDataConverter<ContactList> = {
  toFirestore(contactList: WithFieldValue<ContactList>): DocumentData {
    // @ts-ignore
    return { ...contactList, contacts: contactList.contacts.map((c) => c.ref) };
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): ContactList {
    const data = snapshot.data(options);

    // @ts-ignore
    // todo: default values for non-existing data fields to maintain type safety
    return { ...data, id: snapshot.id, ref: snapshot.ref };
  },
};

export const contactConverter: FirestoreDataConverter<Contact> = {
  toFirestore(contact: WithFieldValue<Contact>): DocumentData {
    return contact;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot, options: SnapshotOptions): Contact {
    const data = snapshot.data(options);

    // if (data.isCollective) {
    // data.creatorUser = await getUser(data.creator);
    // }
    return { ...data, date_created: data?.date_created?.toDate(), id: snapshot.id, ref: snapshot.ref };
  },
};
const UidRequiredError = new Error("uid is required");
export const saveContacts = async (uid: string, contacts: Contact[]) => {
  if (!uid) throw UidRequiredError;
  await Promise.all(
    contacts.map(async (contact) => {
      addDoc(collection(db, `users/${uid}/contacts`), contact);
    })
  );
};

/**
 * Adds a contact to a contact list. If the contact doesn't have a ref, it creates the document in the uid before.
 * @param contacts
 */
export const addContactsToList = async (
  contactListRef: DocumentReference<ContactList>,
  creatorId: string,
  contacts: Contact[]
) => {
  // for each contact, if it doesn't have a ref, create it and add it to the contact in the contact list
  await Promise.all(
    contacts.map(async (contact, idx) => {
      if (contact?.ref) return contact;
      const newContactRef = await addDoc(collection(db, `users/${creatorId}/contacts`), contact);

      contacts[idx] = { ...contact, ref: newContactRef, id: newContactRef.id };
    })
  );

  // add all (now, db-existing) contacts to the contact list
  updateDoc(contactListRef, {
    contacts: arrayUnion(...contacts.map((c) => c.ref)),
  });
};

export const deleteContact = async (contact: Contact, contactOwnerId: string) =>
  contact?.ref ? deleteDoc(contact?.ref) : deleteDoc(doc(db, `users/${contactOwnerId}/contacts/${contact?.id}`));
export const deleteContactList = async (contactList: ContactList, contactOwnerId?: string) =>
  contactList?.ref
    ? deleteDoc(contactList?.ref)
    : deleteDoc(doc(db, `users/${contactOwnerId}/contact_lists/${contactList?.id}`));

export const createContactList = async (creatorId: string, listTitle: string): Promise<ContactList> => {
  const contactListRef = await addDoc(collection(db, `users/${creatorId}/contact_lists`), {
    title: listTitle,
    date_created: new Date(),
    contacts: [],
  });

  return await getDoc(contactListRef).then((doc) => contactListConverter.fromFirestore(doc));
};
export const emailToContact = (email: string): Contact => {
  return { email, date_created: new Date() };
};

export const getContactsByEmails = async (emails: string[], creatorId: string): Promise<Contact[]> => {
  if (emails.length > 10) {
    const chunkNumber = Math.ceil(emails.length / 10);
    let aggregatedContacts = [];
    for (let i = 0; i < chunkNumber; i++) {
      const chunk = emails.slice(i * 10, i * 10 + 10);
      const contacts = await getContactsByEmails(chunk, creatorId);
      aggregatedContacts = aggregatedContacts.concat(contacts);
    }
    return aggregatedContacts;
  } else {
    const q = query(collection(db, `users/${creatorId}/contacts`), where("email", "in", emails));
    return (await getDocs(q)).docs.map((d) => contactConverter.fromFirestore(d));
  }
};

export const getContactList = async (ref: DocumentReference<ContactList>) =>
  (await getDoc(ref.withConverter(contactListConverter))).data();
/**
 * maps emails to existing (or newly created) contacts
 * @param emails
 */
export const saveAndDedupeContacts = async (creatorId: string, emails: string[]): Promise<Contact[]> => {
  const existingContacts = (await getContactsByEmails(emails, creatorId)) || [];

  const existingEmails = existingContacts?.map((c) => c.email) || [];
  // create new contact documents for emails that don't have a contact yet
  const newContacts = await Promise.all(
    emails
      .filter((email) => !existingEmails.includes(email))
      .map(async (email) => {
        const newContactRef = await addDoc(collection(db, `users/${creatorId}/contacts`), emailToContact(email));

        return contactConverter.fromFirestore(await getDoc(newContactRef));
      })
  );

  return [...existingContacts, ...newContacts];
};
